/**
 * Reading a match side, shared by the admin, the projector and the player app.
 *
 * A match is two sides, and a side is a player or a team (POL-82). The server
 * resolves that polymorphism into `sideA`/`sideB`, so the only thing a client
 * has to do is look a side up by A/B — which three files were each doing with
 * their own copy of `nameFor`.
 *
 * Structurally typed on purpose: each app generates its own fragment types
 * from the same schema, and a structural shape lets all three satisfy this
 * without importing each other's codegen.
 */

export type MatchSideKind = "PLAYER" | "TEAM";

export type MatchSideMember = {
  id: string;
  name: string;
  hcp?: number | null;
  strokes?: number | null;
};

export type MatchSideRef = {
  kind: MatchSideKind;
  /** The id the side's score rows are keyed by — a player, or a team. */
  id: string;
  name: string;
  hcp?: number | null;
  /** The side's own allowance. Null in best ball, which has none. */
  handicap?: number | null;
  /** Strokes this side receives in the match. */
  strokes?: number | null;
  players?: readonly MatchSideMember[] | null;
};

export type SidedMatch = {
  sideA?: MatchSideRef | null;
  sideB?: MatchSideRef | null;
};

/** 'A' | 'B' as the schema spells it, without importing the generated enum. */
export type SideKey = "A" | "B";

export const sideOf = (
  match: SidedMatch,
  side: SideKey | null | undefined,
): MatchSideRef | null =>
  side === "A"
    ? (match.sideA ?? null)
    : side === "B"
      ? (match.sideB ?? null)
      : null;

/**
 * The name of a side, or an empty string when there is none to show.
 *
 * Empty rather than a placeholder: every caller renders this inside a sentence
 * it has already composed, and "Uten navn mot Lie / Myhre" reads worse than
 * the gap. The server already substitutes a fallback for a nameless team.
 */
export const nameOfSide = (
  match: SidedMatch,
  side: SideKey | null | undefined,
): string => sideOf(match, side)?.name ?? "";

/** Both sides' ids — what a flight entry is grouped by. */
export const sideIds = (match: SidedMatch): string[] =>
  [match.sideA?.id, match.sideB?.id].filter((id): id is string => Boolean(id));

/** The players on a side. A singles side is a list of one. */
export const memberIds = (side: MatchSideRef | null | undefined): string[] =>
  (side?.players ?? []).map((player) => player.id);

export const isTeamMatch = (match: SidedMatch): boolean =>
  match.sideA?.kind === "TEAM";

/**
 * Which side an entity belongs to — by side id, or by membership.
 *
 * Both axes matter: a flight entry in a one-ball format IS the team, so it
 * matches on side id, while a best-ball entry is a player and matches through
 * the roster. Callers should not have to know which.
 */
export const sideOwning = (
  match: SidedMatch,
  entityId: string | null | undefined,
): SideKey | null => {
  if (!entityId) return null;
  for (const key of ["A", "B"] as const) {
    const side = sideOf(match, key);
    if (!side) continue;
    if (side.id === entityId) return key;
    if (memberIds(side).includes(entityId)) return key;
  }
  return null;
};

/** Every player on either side, for "is this entry in a match at all". */
export const matchMemberIds = (match: SidedMatch): string[] => [
  ...memberIds(match.sideA),
  ...memberIds(match.sideB),
];
